/**
 * Finance — AR Payment Follow-ups / Collections queue.
 *
 * Slimmed in Iter 82. Sub-components extracted to
 * /components/finance/collections/*:
 *   - CustomerDrill (drawer w/ 4 tabs)
 *   - CollectorPerformancePanel (Team Performance tab)
 *   - constants (STATUS_META, CHANNEL_ICON, BUCKET_TONE, fmt)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { HandCoins, RefreshCw, Clock, Trophy } from "lucide-react";
import { toast } from "sonner";
import CustomerDrill from "@/components/finance/collections/CustomerDrill";
import CollectorPerformancePanel from "@/components/finance/collections/CollectorPerformancePanel";
import { fmt, STATUS_META, BUCKET_TONE } from "@/components/finance/collections/constants";
import AdvancedTable from "@/components/table/AdvancedTable";
import { apiErrorMessage } from "@/lib/apiError";

function StatCard({ label, value, tone = "", testid }) {
  return (
    <Card><CardContent className="p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-mono mt-1 ${tone}`} data-testid={testid}>{value}</div>
    </CardContent></Card>
  );
}

export default function FinanceCollections() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [sweeping, setSweeping] = useState(false);

  const loadQueue = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/finance/workspaces/${wsId}/ar/collections-queue`);
      setQueue(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load queue")); }
    finally { setLoading(false); }
  }, [wsId]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const filtered = useMemo(() => {
    let rows = queue?.rows || [];
    if (statusFilter !== "all") rows = rows.filter((r) => r.followup_status === statusFilter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      rows = rows.filter((r) => (r.client_name || "").toLowerCase().includes(needle));
    }
    return rows;
  }, [queue, statusFilter, q]);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const { data } = await api.post(`/finance/workspaces/${wsId}/ar/sweep-promised`);
      toast.success(`Sweep done — ${data.broken_promise} moved to broken promise (of ${data.scanned} scanned).`);
      loadQueue();
    } catch (e) { toast.error(apiErrorMessage(e, "Sweep failed")); }
    finally { setSweeping(false); }
  };

  const openDrill = queue?.rows?.find((r) => r.client_id === openId) || null;

  return (
    <div className="space-y-6" data-testid="finance-collections-page">
      <PageHeader
        title="Receivables"
        description="Aged AR · Collection playbook · Dunning-safe follow-ups."
        subtitle="Track outstanding invoices, log reminders, and send AI-drafted follow-ups via Email / WhatsApp / SMS."
        icon={HandCoins}
      />

      <Tabs defaultValue="queue">
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          <TabsTrigger value="queue" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="collections-tab-queue">
            <HandCoins className="w-4 h-4 mr-2" />Queue
          </TabsTrigger>
          <TabsTrigger value="team" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="collections-tab-team">
            <Trophy className="w-4 h-4 mr-2" />Team Performance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="queue" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <StatCard label="Total outstanding" value={fmt(queue?.total_outstanding || 0)} testid="stat-outstanding" tone="text-rose-600" />
            <StatCard label="Customers in queue" value={queue?.count ?? "—"} testid="stat-count" />
            <StatCard label="Broken promises"
              value={(queue?.rows || []).filter((r) => r.followup_status === "broken_promise").length}
              testid="stat-broken" tone="text-rose-700 font-semibold" />
            <StatCard label="Promised this week"
              value={(queue?.rows || []).filter((r) => r.followup_status === "promised").length}
              testid="stat-promised" tone="text-amber-600" />
          </div>

          <Card><CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[220px]">
                <Label className="text-xs">Search customer</Label>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by customer name…" data-testid="collections-search" />
              </div>
              <div className="min-w-[180px]">
                <Label className="text-xs">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="collections-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {Object.entries(STATUS_META).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={loadQueue} data-testid="collections-refresh">
                <RefreshCw className="w-4 h-4 mr-2" />Refresh
              </Button>
              <Button variant="outline" onClick={runSweep} disabled={sweeping} data-testid="collections-sweep">
                {sweeping ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Clock className="w-4 h-4 mr-2" />}
                Sweep promises
              </Button>
            </div>
          </CardContent></Card>

          <Card><CardContent className="p-0">
            <AdvancedTable
              tableKey="collections-queue"
              title="Payment follow-ups"
              rows={filtered}
              loading={loading}
              columns={[
                {
                  key: "client_name", label: "Customer", width: 220, sortable: true, filterType: "text",
                  accessor: (r) => r.client_name || "",
                  render: (r) => (
                    <div>
                      <div className="font-medium">{r.client_name}</div>
                      {r.assigned_to && <div className="text-xs text-muted-foreground">Assigned</div>}
                    </div>
                  ),
                },
                {
                  key: "outstanding", label: "Outstanding", width: 140, sortable: true, filterType: "number-range",
                  accessor: (r) => r.outstanding || 0,
                  render: (r) => <span className="font-mono text-rose-600">{fmt(r.outstanding)}</span>,
                  align: "right",
                  aggregate: "sum",
                },
                {
                  key: "invoice_count", label: "Invoices", width: 90, sortable: true, filterType: "number-range",
                  accessor: (r) => r.invoice_count || 0,
                  align: "center",
                  aggregate: "sum",
                },
                {
                  key: "oldest_days", label: "Oldest", width: 100, sortable: true, filterType: "number-range",
                  accessor: (r) => r.oldest_days || 0,
                  render: (r) => (
                    <span className={BUCKET_TONE[r.oldest_bucket] || ""}>
                      {r.oldest_bucket === "current" ? "Current" : `${r.oldest_days}d`}
                    </span>
                  ),
                  align: "center",
                },
                {
                  key: "followup_status", label: "Status", width: 130, sortable: true, filterType: "enum",
                  filterOptions: Object.keys(STATUS_META || {}),
                  accessor: (r) => r.followup_status || "",
                  render: (r) => (
                    <span className={`inline-block text-xs px-2 py-0.5 rounded ${STATUS_META[r.followup_status]?.tone || ""}`}>
                      {STATUS_META[r.followup_status]?.label || r.followup_status}
                    </span>
                  ),
                },
                {
                  key: "promised_payment_date", label: "Promised", width: 110, sortable: true, filterType: "date-range",
                  accessor: (r) => r.promised_payment_date || "",
                  render: (r) => <span className="text-xs">{r.promised_payment_date || "—"}</span>,
                },
                {
                  key: "last_reminder_at", label: "Last touch", width: 140, sortable: true, filterType: "date-range",
                  accessor: (r) => r.last_reminder_at || "",
                  render: (r) => (
                    <span className="text-xs text-muted-foreground">
                      {r.last_reminder_at ? new Date(r.last_reminder_at).toLocaleDateString() : "—"}
                      {r.last_channel && <span className="ml-1 opacity-60">({r.last_channel})</span>}
                    </span>
                  ),
                },
              ]}
              emptyIcon={HandCoins}
              emptyTitle="No customers match"
              emptyDescription="Everyone paid up? Try changing filters."
              onRowClick={(r) => setOpenId(r.client_id)}
              renderRowActions={(r) => (
                <Button size="sm" variant="outline" className="rounded-none h-7 px-2" onClick={(e) => { e.stopPropagation(); setOpenId(r.client_id); }} data-testid={`collections-open-${r.client_id}`}>
                  Open
                </Button>
              )}
            />
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="team" className="mt-6">
          <CollectorPerformancePanel wsId={wsId} />
        </TabsContent>
      </Tabs>

      <Sheet open={!!openId} onOpenChange={(v) => !v && setOpenId(null)}>
        <SheetContent side="right" className="w-[560px] sm:max-w-[640px] overflow-y-auto" data-testid="collections-drawer">
          <SheetHeader className="mb-4">
            <SheetTitle>{openDrill?.client_name || "Customer"}</SheetTitle>
            <SheetDescription>Track outstanding balances, log a reminder, or update the follow-up status for this customer.</SheetDescription>
          </SheetHeader>
          {openId && <CustomerDrill wsId={wsId} clientId={openId} onChanged={loadQueue} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
